import { useMemo } from 'react';

import {
  useCurrentBusinessPartnerDivisions,
  useCurrentBusinessPartnerDivisionRequests,
} from '../pages/Divisions/divisions.queries';
import { useCurrentRole, useAppData } from './queries/appSession.queries';
import {
  BusinessPartnerAccessabilityEnum,
  BusinessPartnerWithCurrentIdentityInfoBody,
} from '../api/generated/pap';
import { RequestBusinessPartnerStatusEnum } from '../api/generated/pm';
import { UserRole } from '../types';
import { BusinessPartnerType } from '../app/routes/utils';

export function useAggregateDivisions() {
  const {
    data: divisions,
    isLoading: isLoadingDivisions,
    isFetched: isFetchedDivisions,
    refetch: refetchDivisions,
  } = useCurrentBusinessPartnerDivisions();
  const { data: roles, isFetched: loadedRoles } = useCurrentRole();
  const userRoles = (roles?.items || []) as UserRole[];
  const isBusinessPartnerAdmin = userRoles.includes(UserRole.BusinessPartnerAdministrator);
  const { currentBusinessPartner, loadedBusinessPartner } = useAppData();
  const shouldFetchDivisionRequests =
    loadedRoles &&
    isBusinessPartnerAdmin &&
    loadedBusinessPartner &&
    currentBusinessPartner &&
    currentBusinessPartner.type === BusinessPartnerType.EndCustomerEnterprise;
  const {
    data: divisionRequests,
    isFetched: isFetchedDivisionRequests,
    refetch: refetchDivisionRequests,
  } = useCurrentBusinessPartnerDivisionRequests(shouldFetchDivisionRequests);

  const combinedDivisions = useMemo(() => {
    if (!divisionRequests?.items && !divisions?.items) {
      return [];
    }

    const requestDivisions = (divisionRequests?.items || [])
      .filter(
        (request) =>
          request.status !== RequestBusinessPartnerStatusEnum.ACCEPTED &&
          request.status !== RequestBusinessPartnerStatusEnum.DONE
      )
      .map(
        (request): BusinessPartnerWithCurrentIdentityInfoBody => ({
          id: request.id,
          name: request.name,
          accessability: BusinessPartnerAccessabilityEnum.REQUEST_TO_JOIN,
          archived: false,
          extId: '',
          iconURL: undefined,
          isOwner: false,
          isSameDomain: false,
          modifiedAt: request.createdAt,
          createdAt: request.createdAt,
          ownerIdentityId: request.ownerIdentityId,
          parentBusinessPartnerId: request.parentBusinessPartnerId,
          trn: '',
          type: request.type,
          requestToJoin: {
            businessPartnerId: request.id,
            createdAt: request.createdAt,
            id: request.id,
            identityId: request.ownerIdentityId,
            modifiedAt: request.modifiedAt,
          },
        })
      );

    const activeDivisions = (divisions?.items || []).filter((division) => !division.archived);

    return [...requestDivisions, ...activeDivisions].sort((a, b) =>
      a.name.localeCompare(b.name, 'fi', { numeric: true })
    );
  }, [divisions, divisionRequests]);

  const isLoading =
    isLoadingDivisions ||
    !isFetchedDivisions ||
    (shouldFetchDivisionRequests && !isFetchedDivisionRequests);

  const refetch = async () => {
    await Promise.all([
      refetchDivisions(),
      shouldFetchDivisionRequests ? refetchDivisionRequests() : Promise.resolve(),
    ]);
  };

  return {
    divisions: combinedDivisions,
    isLoading,
    isFetched: isFetchedDivisions && (!shouldFetchDivisionRequests || isFetchedDivisionRequests),
    refetch,
  };
}
